import { BasicButton, EmailInput, Loading } from "@/components";
import useDeeplinkAttempt from "@/hooks/useDeeplinkAttempt";
import { forgotPassword } from "@/redux/reducers/auth";
import { VerificationSearchParamsSchema } from "@/schemas";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Toolbar from "@mui/material/Toolbar";
import { Link, createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { useCallback, useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

export const Route = createFileRoute("/forgot-password")({
	validateSearch: zodSearchValidator(VerificationSearchParamsSchema),
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation("common");
	const navigate = Route.useNavigate();
	const search = Route.useSearch({
		select: (s: VerificationSearchParamsSchema) => s,
	});
	const resetPasswordToken = search.token ?? search.reset_password_token;
	const dispatch = useDispatch();
	const [form, setForm] = useReducer(
		(state: any, newState: any) => ({ ...state, ...newState }),
		{
			email: "",
		},
	);
	const alertMessage = useSelector(
		(state: any) => state.auth?.forgotPassword.error?.errors?.[0],
	);
	const [buttonDisable, setButtonDisable] = useState(true);
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (form.email) {
			setButtonDisable(false);
		} else {
			setButtonDisable(true);
		}
	}, [form, alertMessage]);

	const handleSubmit = useCallback(() => {
		setLoading(true);
		dispatch(
			forgotPassword({
				email: form.email,
				callback: () => {
					setOpen(true);
					setLoading(false);
				},
			}),
		);
	}, [dispatch, form.email]);

	useEffect(() => {
		if (alertMessage) {
			setLoading(false);
		}
	}, [alertMessage]);

	useDeeplinkAttempt(`forgot-password?token=${resetPasswordToken}`, {
		disabled: !resetPasswordToken,
		onDesktop: () =>
			navigate({
				replace: true,
				to: "/verification",
				search,
			}),
		onFailed: () =>
			navigate({
				replace: true,
				to: "/verification",
				search,
			}),
	});

	return (
		<>
			<div className="container wrapper">
				<AppBar position="fixed" className="!bg-white text-dark !shadow-none">
					<Toolbar variant="dense" className="wrapper w-full h-14">
						<Link to={"/"}>
							<IconButton
								edge="start"
								aria-label="backspace"
								className="mr-2 text-2xl"
							>
								<KeyboardBackspaceIcon className="text-dark" />
							</IconButton>
						</Link>
					</Toolbar>
				</AppBar>
				<Box className="mt-12">
					<h1 className="w-full text-[28px] font-semibold py-3 mb-4">
						{t("forgotPassword.title")}
					</h1>
					<Box className="w-full md:max-w-[358px] flex flex-col items-center mx-auto">
						<EmailInput
							value={form.email}
							errorMessage={alertMessage}
							onChange={(e) => setForm({ email: e.target.value })}
						/>
						<BasicButton
							variant="contained"
							color="secondary"
							className="w-full h-14 !text-base !shadow-none !mt-8"
							disable={buttonDisable}
							onClick={handleSubmit}
						>
							{t("forgotPassword.submit")}
						</BasicButton>
					</Box>
				</Box>
			</div>
			{/* Modal */}
			<Modal
				open={open}
				onClose={() => setOpen(false)}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box className="h-dvh flex justify-center items-center">
					<Box className="w-[310px] h-[168px] bg-white rounded-2xl p-4">
						<p className="text-dark text-base text-center font-semibold mb-2">
							{t("forgotPassword.successTitle")}
						</p>
						<p className="text-gray-100 text-sm text-center mb-4">
							{t("forgotPassword.successMessage")}
						</p>
						<BasicButton
							variant="contained"
							color="secondary"
							className="w-full !h-12 !text-sm !shadow-none"
							onClick={() => setOpen(false)}
						>
							{t("ok")}
						</BasicButton>
					</Box>
				</Box>
			</Modal>
			<Loading status={loading} />
		</>
	);
}
